import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../services/api/apiService";
import { useAuth } from "../../contexts/AuthContext";
import { useWebSocket } from "../../contexts/WebSocketContext";
import { getNotifications, markNotificationsRead } from "../../services/api/notificationService";
import { playNotificationSound } from "../../utils/notificationSound";
import { eid } from "../../utils/entityId";
import { getNotificationLink, isInviteNotification } from "../../utils/notificationLinks";
import "../../styles/features/matches.css";

/**
 * NotificationBell
 * Bell icon in the nav bar; the badge shows the unread notifications count.
 * Dropdown has two tabs: Challenges (with inline Accept/Decline) | Notifications.
 * Auth is cookie-based — no bearer token needed.
 */
const NotificationBell = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const webSocket = useWebSocket();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("challenges");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [challenges, setChallenges] = useState([]);
  const [responding, setResponding] = useState(null);
  const [tournamentInvitations, setTournamentInvitations] = useState([]);
  const [respondingInvite, setRespondingInvite] = useState(null);
  const [teamInvitations, setTeamInvitations] = useState([]);
  const [respondingTeamInvite, setRespondingTeamInvite] = useState(null);
  const [friendRequests, setFriendRequests] = useState([]);
  const [respondingFriend, setRespondingFriend] = useState(null);
  const dropdownRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await getNotifications({ limit: 15 });
      setNotifications(res.data?.data?.notifications || []);
      setUnreadCount(res.data?.data?.unread_count || 0);
    } catch {
      // silent
    }
  }, [isAuthenticated]);

  const loadChallenges = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await apiClient.get("/matches/my-challenges");
      setChallenges(res.data?.data || []);
    } catch {
      setChallenges([]);
    }
  }, [isAuthenticated]);

  const loadTournamentInvitations = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await apiClient.get("/teams/tournament-invitations?status=pending");
      const list = res.data?.data;
      setTournamentInvitations(Array.isArray(list) ? list : []);
    } catch {
      setTournamentInvitations([]);
    }
  }, [isAuthenticated]);

  const loadFriendRequests = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await apiClient.get("/friends/requests?limit=20");
      const list = res.data?.data?.requests;
      setFriendRequests(Array.isArray(list) ? list : []);
    } catch {
      setFriendRequests([]);
    }
  }, [isAuthenticated]);

  const loadTeamInvitations = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await apiClient.get("/teams/invitations?status=pending");
      const list = res.data?.data?.invitations ?? res.data?.data;
      setTeamInvitations(Array.isArray(list) ? list : []);
    } catch {
      setTeamInvitations([]);
    }
  }, [isAuthenticated]);

  // Initial load + poll every 60 s (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;
    loadNotifications();
    loadChallenges();
    loadTournamentInvitations();
    loadFriendRequests();
    loadTeamInvitations();
    const interval = setInterval(() => {
      loadNotifications();
      loadChallenges();
      loadTournamentInvitations();
      loadFriendRequests();
      loadTeamInvitations();
    }, 60_000);
    return () => clearInterval(interval);
  }, [isAuthenticated, loadNotifications, loadChallenges, loadTournamentInvitations, loadFriendRequests, loadTeamInvitations]);

  // Real-time delivery: the backend emits `notification:new` over the existing
  // WebSocket connection (respecting the recipient's push/type preferences).
  // Prepend it locally, bump the badge, and — if enabled — play a sound and/or
  // show a native browser notification (no FCM/APNs wiring needed for this).
  useEffect(() => {
    if (!isAuthenticated || !webSocket.connected) return;

    const removeListener = webSocket.addEventListener("notification:new", (payload) => {
      const notification = payload?.notification;
      if (!notification) return;

      setNotifications((prev) => [notification, ...prev].slice(0, 15));
      setUnreadCount((prev) => prev + 1);

      if (payload.playSound) {
        playNotificationSound();
      }
      if (typeof window !== "undefined" && window.Notification && window.Notification.permission === "granted") {
        try {
          // eslint-disable-next-line no-new
          new window.Notification(notification.title, { body: notification.message });
        } catch {
          // Notification API can throw in some embedded/webview contexts — ignore.
        }
      }
    });

    return removeListener;
  }, [isAuthenticated, webSocket]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleToggle = async () => {
    const nowOpen = !open;
    setOpen(nowOpen);
    if (nowOpen) {
      loadNotifications();
      loadChallenges();
      loadTournamentInvitations();
      loadFriendRequests();
      loadTeamInvitations();
    }
  };

  // Mark alerts read only once the Alerts tab is actually on screen — opening
  // the bell on the default Challenges tab must not silently clear them.
  useEffect(() => {
    if (!open || activeTab !== "notifications" || unreadCount === 0) return;
    markNotificationsRead()
      .then(() => {
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      })
      .catch(() => {});
  }, [open, activeTab, unreadCount]);

  const respond = async (matchId, action) => {
    setResponding(matchId);
    try {
      await apiClient.post(`/matches/${matchId}/respond-challenge`, { action });
      await loadChallenges();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to respond to challenge");
    } finally {
      setResponding(null);
    }
  };

  const handleNotifClick = (notif) => {
    // Team invitations have no page of their own — answer them in the Invites tab
    if (!notif.data?.link && isInviteNotification(notif)) {
      setActiveTab("invitations");
      return;
    }
    // data.link (in-app only) → match_id → type-based fallback route
    const target = getNotificationLink(notif);
    setOpen(false);
    if (target) navigate(target);
  };

  const formatTime = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return new Date(ts).toLocaleDateString();
  };

  const formatDate = (dt) => {
    if (!dt) return "TBD";
    return new Date(dt).toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const respondToTeamInvitation = async (invitationId, action) => {
    setRespondingTeamInvite(invitationId);
    // Optimistic removal
    setTeamInvitations((prev) => prev.filter((i) => i.id !== invitationId));
    try {
      await apiClient.put(`/teams/invitations/${invitationId}`, { action });
    } catch (err) {
      // Roll back on error
      await loadTeamInvitations();
      alert(err.response?.data?.message || "Failed to respond to team invitation");
    } finally {
      setRespondingTeamInvite(null);
    }
  };

  const respondToInvitation = async (invitationId, action) => {
    setRespondingInvite(invitationId);
    try {
      await apiClient.put(`/teams/tournament-invitations/${invitationId}/respond`, { action });
      setTournamentInvitations((prev) => prev.filter((i) => i.id !== invitationId));
    } catch (err) {
      alert(err.response?.data?.message || "Failed to respond to invitation");
    } finally {
      setRespondingInvite(null);
    }
  };

  const respondToFriendRequest = async (requesterId, action) => {
    setRespondingFriend(requesterId);
    try {
      if (action === "accept") {
        await apiClient.put(`/friends/${requesterId}/accept`);
      } else {
        await apiClient.put(`/friends/${requesterId}/reject`);
      }
      setFriendRequests((prev) => prev.filter((r) => r.requester_id !== requesterId));
    } catch (err) {
      alert(err.response?.data?.message || "Failed to respond to friend request");
    } finally {
      setRespondingFriend(null);
    }
  };

  const incomingChallenges = challenges.filter((c) => c.can_respond);
  // One count source for the bell badge: unread notifications. Challenges,
  // team/tournament/league invites and friend requests each already create a
  // notification row when they arrive, so adding the pending-list lengths on
  // top counted every such item twice. The per-tab badges below still show
  // how many items are waiting for a response in each tab.
  const totalBadge = unreadCount;

  if (!isAuthenticated) return null;

  return (
    <div className="notification-bell-wrapper" ref={dropdownRef}>
      <button
        className="floating-nav-btn notification-bell-btn"
        onClick={handleToggle}
        aria-label={`Notifications${totalBadge ? ` (${totalBadge})` : ""}`}
        title="Notifications & Challenges"
      >
        <span className="nav-icon">🔔</span>
        {totalBadge > 0 && (
          <span className="notification-badge">{totalBadge > 9 ? "9+" : totalBadge}</span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown">
          {/* Tab bar */}
          <div className="notif-tab-bar">
            <button
              className={`notif-tab${activeTab === "challenges" ? " notif-tab--active" : ""}`}
              onClick={() => setActiveTab("challenges")}
            >
              ⚔️ Challenges
              {incomingChallenges.length > 0 && (
                <span className="notif-tab-badge">{incomingChallenges.length}</span>
              )}
            </button>
            <button
              className={`notif-tab${activeTab === "invitations" ? " notif-tab--active" : ""}`}
              onClick={() => setActiveTab("invitations")}
            >
              🏆 Invites
              {(tournamentInvitations.length + teamInvitations.length) > 0 && (
                <span className="notif-tab-badge">{tournamentInvitations.length + teamInvitations.length}</span>
              )}
            </button>
            <button
              className={`notif-tab${activeTab === "friends" ? " notif-tab--active" : ""}`}
              onClick={() => setActiveTab("friends")}
            >
              👥 Friends
              {friendRequests.length > 0 && (
                <span className="notif-tab-badge">{friendRequests.length}</span>
              )}
            </button>
            <button
              className={`notif-tab${activeTab === "notifications" ? " notif-tab--active" : ""}`}
              onClick={() => setActiveTab("notifications")}
            >
              🔔 Alerts
              {unreadCount > 0 && (
                <span className="notif-tab-badge">{unreadCount}</span>
              )}
            </button>
          </div>

          {/* ── Challenges tab ── */}
          {activeTab === "challenges" && (
            <>
              {incomingChallenges.length === 0 ? (
                <div className="notification-empty">No pending challenges</div>
              ) : (
                incomingChallenges.map((c) => {
                  const challengerName = c.challenger_team_name || c.team1_name || c.challenger_name || c.challenger_username || "Challenger";
                  const challengedName = c.challenged_team_name || c.team2_name || c.challenged_name || c.challenged_username || "You";
                  return (
                    <div key={c.id} className={`notif-challenge-item${c.can_respond ? " notif-challenge--incoming" : ""}`}>
                      <div className="notif-challenge-teams">
                        <span className="notif-challenger">{challengerName}</span>
                        <span className="notif-vs"> ⚔️ </span>
                        <span className="notif-challenged">{challengedName}</span>
                      </div>
                      <div className="notif-challenge-meta">
                        {c.sport_name && <span>{c.sport_name}</span>}
                        {c.scheduled_at && <span> · 📅 {formatDate(c.scheduled_at)}</span>}
                      </div>
                      <div className="notif-challenge-actions">
                        {c.can_respond ? (
                          <>
                            <button
                              className="notif-btn notif-btn--accept"
                              disabled={responding === c.id}
                              onClick={() => respond(c.id, "accept")}
                            >
                              {responding === c.id ? "…" : "✅ Accept"}
                            </button>
                            <button
                              className="notif-btn notif-btn--decline"
                              disabled={responding === c.id}
                              onClick={() => respond(c.id, "decline")}
                            >
                              {responding === c.id ? "…" : "❌ Decline"}
                            </button>
                          </>
                        ) : (
                          <span className="notif-waiting">⏳ Awaiting response</span>
                        )}
                        <button
                          className="notif-btn notif-btn--view"
                          onClick={() => { setOpen(false); navigate(`/matches/${eid(c)}`); }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}

          {/* ── Invites tab: team + tournament/league invitations ── */}
          {activeTab === "invitations" && (
            <>
              {/* Team invite sub-section */}
              {teamInvitations.length > 0 && (
                <>
                  <div className="notif-section-label">👥 Team Invitations</div>
                  {teamInvitations.map((inv) => {
                    const isBusy = respondingTeamInvite === inv.id;
                    return (
                      <div key={inv.id} className="notif-challenge-item notif-challenge--incoming">
                        <div className="notif-challenge-teams">
                          <span>👥</span>
                          <span style={{ marginLeft: "0.4rem", fontWeight: 600 }}>{inv.team_name || "A team"}</span>
                        </div>
                        <div className="notif-challenge-meta">
                          {inv.sport_type && <span>{inv.sport_type}</span>}
                          {inv.inviter_username && <span> · By @{inv.inviter_username}</span>}
                          {inv.invited_at && <span> · {formatTime(inv.invited_at)}</span>}
                        </div>
                        {inv.message && (
                          <div className="notif-challenge-meta" style={{ fontStyle: "italic" }}>💬 {inv.message}</div>
                        )}
                        <div className="notif-challenge-actions">
                          <button
                            className="notif-btn notif-btn--accept"
                            disabled={isBusy}
                            onClick={() => respondToTeamInvitation(inv.id, "accept")}
                          >
                            {isBusy ? "…" : "✅ Accept"}
                          </button>
                          <button
                            className="notif-btn notif-btn--decline"
                            disabled={isBusy}
                            onClick={() => respondToTeamInvitation(inv.id, "decline")}
                          >
                            {isBusy ? "…" : "❌ Decline"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
              {/* Tournament / league invite sub-section */}
              {tournamentInvitations.length > 0 && (
                <>
                  <div className="notif-section-label">🏆 Tournament & League Invites</div>
                  {tournamentInvitations.map((inv) => {
                    const contextName = inv.tournament_name || inv.league_name || "Tournament";
                    const contextSport = inv.tournament_sport || inv.league_sport;
                    const isTournament = !!inv.tournament_id;
                    return (
                      <div key={inv.id} className="notif-challenge-item notif-challenge--incoming">
                        <div className="notif-challenge-teams">
                          <span>{isTournament ? "🏆" : "🏅"}</span>
                          <span style={{ marginLeft: "0.4rem", fontWeight: 600 }}>{contextName}</span>
                        </div>
                        <div className="notif-challenge-meta">
                          {contextSport && <span>{contextSport}</span>}
                          <span> · By {inv.invited_by_name || inv.invited_by_username || "Organizer"}</span>
                        </div>
                        {inv.message && (
                          <div className="notif-challenge-meta" style={{ fontStyle: "italic" }}>
                            💬 {inv.message}
                          </div>
                        )}
                        <div className="notif-challenge-actions">
                          <button
                            className="notif-btn notif-btn--accept"
                            disabled={respondingInvite === inv.id}
                            onClick={() => respondToInvitation(inv.id, "accept")}
                          >
                            {respondingInvite === inv.id ? "…" : "✅ Accept"}
                          </button>
                          <button
                            className="notif-btn notif-btn--decline"
                            disabled={respondingInvite === inv.id}
                            onClick={() => respondToInvitation(inv.id, "decline")}
                          >
                            {respondingInvite === inv.id ? "…" : "❌ Decline"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {teamInvitations.length === 0 && tournamentInvitations.length === 0 && (
                <div className="notification-empty">No pending invites</div>
              )}
            </>
          )}

          {/* ── Friend Requests tab ── */}
          {activeTab === "friends" && (
            <>
              {friendRequests.length === 0 ? (
                <div className="notification-empty">No pending friend requests</div>
              ) : (
                friendRequests.map((req) => {
                  const fullName = [req.first_name, req.last_name].filter(Boolean).join(" ") || req.username || "Unknown";
                  const userId = req.requester_short_code || req.requester_uuid || req.requester_user_id || req.requester_id;
                  const isBusy = respondingFriend === req.requester_id;
                  return (
                    <div key={req.id} className="notif-challenge-item notif-challenge--incoming notif-friend-request">
                      <div className="notif-friend-info">
                        {req.avatar_url ? (
                          <img
                            src={req.avatar_url}
                            alt={fullName}
                            className="notif-friend-avatar"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        ) : (
                          <div className="notif-friend-avatar notif-friend-avatar--initials">
                            {fullName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                          </div>
                        )}
                        <div className="notif-friend-meta">
                          <span className="notif-friend-name">{fullName}</span>
                          {req.username && <span className="notif-friend-username">@{req.username}</span>}
                          <span className="notif-friend-time">{formatTime(req.created_at)}</span>
                        </div>
                      </div>
                      <div className="notif-challenge-actions">
                        <button
                          className="notif-btn notif-btn--accept"
                          disabled={isBusy}
                          onClick={() => respondToFriendRequest(req.requester_id, "accept")}
                        >
                          {isBusy ? "…" : "✅ Accept"}
                        </button>
                        <button
                          className="notif-btn notif-btn--decline"
                          disabled={isBusy}
                          onClick={() => respondToFriendRequest(req.requester_id, "reject")}
                        >
                          {isBusy ? "…" : "❌ Decline"}
                        </button>
                        <button
                          className="notif-btn notif-btn--view"
                          onClick={() => { setOpen(false); navigate(`/players/${userId}`); }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}

          {/* ── Notifications tab ── */}
          {activeTab === "notifications" && (
            <>
              <div className="notification-dropdown-header">
                <span>Recent Alerts</span>
                {notifications.some((n) => !n.is_read) && (
                  <button
                    className="notif-mark-all-btn"
                    onClick={async () => {
                      try {
                        await markNotificationsRead();
                        setUnreadCount(0);
                        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
                      } catch {}
                    }}
                  >
                    Mark all read
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="notification-empty">No notifications yet</div>
              ) : (
                notifications.map((n) => {
                  // Inline Accept/Reject for role upgrade requests (superadmin only)
                  if (n.type === "role_upgrade_request") {
                    const requestId = n.data?.request_id;
                    return (
                      <div key={n.id} className={`notif-challenge-item notif-challenge--incoming${n.is_read ? "" : " unread"}`}>
                        <div className="notif-challenge-teams">
                          <span>🏅</span>
                          <span style={{ marginLeft: "0.4rem", fontWeight: 600 }}>{n.title}</span>
                        </div>
                        <div className="notif-challenge-meta">{n.message}</div>
                        {requestId && (
                          <div className="notif-challenge-actions">
                            <button className="notif-btn notif-btn--accept" onClick={async () => {
                              try {
                                await apiClient.put(`/users/upgrade-requests/${requestId}/respond`, { action: "approve" });
                                await loadNotifications();
                              } catch (err) { alert(err.response?.data?.message || "Failed"); }
                            }}>✅ Approve</button>
                            <button className="notif-btn notif-btn--decline" onClick={async () => {
                              try {
                                await apiClient.put(`/users/upgrade-requests/${requestId}/respond`, { action: "reject" });
                                await loadNotifications();
                              } catch (err) { alert(err.response?.data?.message || "Failed"); }
                            }}>❌ Reject</button>
                          </div>
                        )}
                        <div className="notif-time">{formatTime(n.created_at)}</div>
                      </div>
                    );
                  }
                  // Default notification
                  return (
                    <div
                      key={n.id}
                      className={`notification-item${n.is_read ? "" : " unread"}`}
                      onClick={() => handleNotifClick(n)}
                    >
                      <div className="notif-title">{n.title}</div>
                      <div className="notif-message">{n.message}</div>
                      <div className="notif-time">{formatTime(n.created_at)}</div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
